import { useEffect, useRef, useState } from "react";

import { useIsMobile } from "../../hooks/useIsMobile";
import { useLocale } from "../../i18n/LocaleContext";
import { LOCALES } from "../../i18n/locales";
import type { AppLocale } from "../../i18n/types";
import Modal from "../Modal/Modal";
import FlagCircle from "./FlagCircle";

function LanguageSwitcher() {
  const { locale, setLocale, t } = useLocale();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current = LOCALES.find((item) => item.code === locale) ?? LOCALES[0];

  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }

    function handleClickOutside(event: MouseEvent) {
      if (
        rootRef.current &&
        event.target instanceof Node &&
        !rootRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isMobile]);

  const handleSelect = (code: AppLocale) => {
    setLocale(code);
    setIsOpen(false);
  };

  const renderOptionList = (lightSurface: boolean) => (
    <ul className="flex flex-col gap-1 p-1" role="listbox">
      {LOCALES.map((item) => {
        const selected = item.code === locale;
        return (
          <li key={item.code}>
            <button
              type="button"
              role="option"
              aria-selected={selected}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                lightSurface
                  ? selected
                    ? "bg-yellow/40 text-dark-2"
                    : "text-dark-2 hover:bg-dark/5"
                  : selected
                    ? "bg-white/20 text-white ring-1 ring-inset ring-green"
                    : "text-white hover:bg-white/10"
              }`}
              onClick={() => handleSelect(item.code)}
              data-umami-event="Language Select"
              data-umami-event-locale={item.code}
            >
              <FlagCircle variant={item.flagVariant} stripes={item.stripes} />
              <span className="flex-1">{t(item.nameKey)}</span>
              <span
                className={`text-xs uppercase tracking-wide ${
                  lightSurface ? "text-dark-3" : "text-white/80"
                }`}
              >
                {item.shortCode}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <div className="relative shrink-0" ref={rootRef}>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full border border-white/25 bg-white/10 py-1 pl-1 pr-3 text-white shadow-sm backdrop-blur-sm transition hover:bg-white/15"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          data-umami-event="Language Switcher Toggle"
        >
          <FlagCircle variant={current.flagVariant} stripes={current.stripes} />
          <span className="text-xs font-bold uppercase tracking-wider">
            {current.shortCode}
          </span>
        </button>

        {isOpen && !isMobile && (
          <div className="absolute right-0 top-[calc(100%+0.5rem)] z-[60] min-w-[12.5rem] rounded-2xl border border-white/15 bg-dark p-1 shadow-xl">
            {renderOptionList(false)}
          </div>
        )}
      </div>

      {isMobile && (
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          id="language-switcher-modal"
          closeIcon="orange"
          modalClassName="max-w-[20rem] !p-6 !py-8"
          containerClassName="w-full text-dark-2"
        >
          <p className="mb-4 text-center text-lg font-extrabold text-dark-2">
            {t("lang.modalTitle")}
          </p>
          {renderOptionList(true)}
        </Modal>
      )}
    </>
  );
}

export default LanguageSwitcher;
